import { useEffect } from 'react';
import { usePathname, useSearchParams } from '@/routing/navigation';
import { initAnalytics, syncAnalyticsUser, trackPageView, isAnalyticsEnabled } from '@/lib/services/analytics';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * Client analytics provider (Mixpanel via shared `analytics` module).
 *
 * Handles:
 * - Initialization on mount
 * - Page view tracking on route changes
 * - User identification when auth state changes
 */
export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    initAnalytics();
    // Eagerly identify from localStorage in the same tick as init,
    // so no recording frame is ever attributed to $device: for a returning user.
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.id) syncAnalyticsUser(cached);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Track page views on route changes
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;

    // Combine pathname and search params for full URL tracking
    const url = searchParams?.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname;

    trackPageView(url);
  }, [pathname, searchParams]);

  // Cross-tab auth sync — mount-time identity already handled by the init effect above
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        if (e.newValue) {
          try {
            const user = JSON.parse(e.newValue);
            if (user?.id) syncAnalyticsUser(user);
          } catch {
            /* ignore */
          }
        } else {
          syncAnalyticsUser(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return <>{children}</>;
}

export default AnalyticsProvider;
